import type { sortOptions } from '~/api/shop/catalogItems/indexing'
import type { BeerDetailsRow } from '~/api/shop/catalogItems/utils'
import type { GoogleMapsComponentsConfig } from '~/api/shop/integrations/integration'
import type { SizeIconValues } from '~/api/shop/integrations/integrationHelpers'
import type {
  ut_catalog_items_type,
  ut_shop_categories_category_type,
  ut_shop_vendors_name,
} from '~/prismaInterfaces'
import type { GetCatalogItemResponse } from './apiV2'

export type AlgoliaCatalogItemType =
  | 'Beer'
  | 'Box'
  | 'Merch'
  | 'SubscriptionBox'

export interface AlgoliaCatalogItem {
  objectID: string
  id: number
  type: AlgoliaCatalogItemType
  name: string
  beer_name: string | null
  brewery_name: string | null
  brewery_state: string | null
  style: string | null
  product_image: string
  vendor: ut_shop_vendors_name
  categories: string[]
  admin_categories: string[]
  untappd_id: number | null
  untappd_rating: number | null
  product_image_dominant_color?: string | null
  merchant_name?: string
  merchant_image?: string
  description?: string
  vendor_item_id: string
  has_variants: boolean
  variants: CatalogItemVariant[]
  variants_in_stock: CatalogItemVariant[]
  lowest_price_variant?: CatalogItemVariant
  filter_type: CatalogItemType
  status: 'enabled' | 'disabled' | 'unknown'
  synced_at?: Date
  created_at?: Date
  updated_at?: Date
  is_out_of_stock?: boolean
  is_hidden?: boolean
  abv?: number
  ibu?: number
  'styles.lvl0'?: string
  'styles.lvl1'?: string
  has_community_award: boolean
  locations?: string[]
  pre_sale_date?: Date | string | null
}

export function isAlgoliaCatalogItem(
  item: CatalogItemResponse | AlgoliaCatalogItem
): item is AlgoliaCatalogItem {
  return (item as AlgoliaCatalogItem).objectID !== undefined
}

export interface CatalogItemResponse {
  id: number
  type: CatalogItemType
  vendor: ut_shop_vendors_name
  vendor_item_id: string
  name: string
  merchant_name: string
  description: string
  product_image: string
  product_images: string[]
  product_image_dominant_color: string | null
  product_image_url: string // The url we have cached in database
  updated_at: Date
  created_at: Date
  status: 'enabled' | 'disabled' | 'unknown'
  variants: CatalogItemVariant[]
  item_details:
    | CatalogItemBeerDetails
    | CatalogItemBoxDetails
    | CatalogItemMerchDetails
  category?: string
  is_out_of_stock?: boolean
  is_hidden?: boolean
  item_is_on_current_catalog?: boolean // Deprecated: v2 API (multi-cart) app will not use this
  similar_items?: AlgoliaCatalogItem[]
  availability_description?: string
  parent_id?: number
  is_available_for_sale?: boolean
  is_mystery_box?: boolean
  pre_sale_date: Date | null
}

export interface CatalogResponseItem {
  name: string
  items: AlgoliaCatalogItem[]
  catalog_items_count: number
  filter_type: CatalogItemType
  category_type?: ut_shop_categories_category_type
  bids?: number[]
}

export type CatalogItemType =
  | 'CatalogItem::Beer'
  | 'CatalogItem::Box'
  | 'CatalogItem::Merch'
  | 'CatalogItem::SubscriptionBox'

export interface BoxBeer extends BeerDetailsRow {
  allergen?: string
  brewery_name: string
}

export interface CatalogItemVariant {
  id: number | string
  name: string
  status: string
  sku: string
  price: number
  sale_price: number
  original_price: number
  type_id: string
  price_formatted: string
  sale_price_formatted: string
  original_price_formatted: string
  icon_url?: string
  icon?: SizeIconValues
  size: string
  is_personalized_box_eligible: boolean
  is_out_of_stock: boolean
  is_on_sale?: boolean
  quantity?: number
  min_qty?: number
}

export interface CatalogItemBeerDetails {
  beer?: BeerDetailsRow
}

export interface CatalogItemBoxDetails {
  beers: BoxBeer[]
  description?: string
  /**
   * @deprecated Use top-level pre_sale_date. Kept for legacy app versions.
   */
  shipping_date?: string
  curated_by?: string
  curated_by_image?: string
}

export interface CatalogItemMerchDetails {
  category: string
  is_untappd_exclusive: boolean
  untappd_exclusive_image_url: string
}

export type CatalogItemBeer = GetCatalogItemResponse & {
  type: 'CatalogItem::Beer'
  item_details: CatalogItemBeerDetails
}

export type CatalogItemBox = GetCatalogItemResponse & {
  type: 'CatalogItem::Box'
  item_details: CatalogItemBoxDetails
}

export type CatalogItemMerch = GetCatalogItemResponse & {
  type: 'CatalogItem::Merch'
  item_details: CatalogItemMerchDetails
}

export interface CartItemResponse {
  catalogItemId: number
  id: number | string
  is_qty_in_stock: boolean
  /**
   * For beer this is the brewery name
   */
  merchant_name: string
  name: string
  price: number // lowest price
  discount_price?: number
  price_formatted: string
  discount_price_formatted?: string
  sale_price: number
  sale_price_formatted: string
  original_price: number
  original_price_formatted: string
  total_price: number // price * qty
  total_price_formatted: string
  total_price_without_deposit_formatted?: string
  product_image_dominant_color?: string | null
  product_image: string
  qty: number
  sku: string
  size: string
  source: 'shop' | 'personalized_box'
  custom_attributes?: {
    product_id?: number
  }
  catalog_item?: Pick<CatalogItemResponse, 'id' | 'type' | 'vendor_item_id'> & {
    beer_id?: number
    beer_name?: string
    brewery_name?: string
    beer_style?: string
  }
  deposit?: {
    name: string
    price: number
    price_formatted: string
  }
  icon?: SizeIconValues
  inventory_quantity?: number
}

export interface CartInfo {
  message?: string
  sku: string
  available?: boolean
}

export interface CreditCard {
  id: string
  last4: string
  type: string
}

export type SortingOptionValues = keyof typeof sortOptions

export interface State {
  name: string
  code: string
}

export interface CheckoutSummaryResponse {
  cart_id: string
  cart: Record<string, CartInfo>
  items: CartItemResponse[]
  grand_total: number
  grand_total_formatted: string
  subtotal: number
  subtotal_formatted: string
  tax_amount: number
  tax_amount_formatted: string
  subtotal_incl_tax: number
  subtotal_incl_tax_formatted: string
  shipping_amount: number
  shipping_amount_formatted: string
  small_order_fee: number
  small_order_fee_formatted: string
  vendor_callout: string
  vendor_name: string
  vendor_email: string
  vendor_phone: string | undefined
  vendor_shipping_information: string
  vendor_logo_url: string
  discount?: { code: string; amount: number; amountFormatted: string }
  segments: { title: string; value: number; valueFormatted: string }[]
  stock_issue?: string
}

export const isCatalogItemBeer = (
  item: CatalogItemResponse | GetCatalogItemResponse
): item is CatalogItemBeer => {
  return item.type === 'CatalogItem::Beer'
}

export const isCatalogItemBox = (
  item: CatalogItemResponse | GetCatalogItemResponse
): item is CatalogItemBox => {
  return item.type === 'CatalogItem::Box'
}

export const isCatalogItemMerch = (
  item: CatalogItemResponse | GetCatalogItemResponse
): item is CatalogItemMerch => {
  return item.type === 'CatalogItem::Merch'
}

export interface Refund {
  id: number
  amount_refunded: number
  amount_refunded_formatted: string
  created_at: string
  updated_at: string
}

export interface OrderResponse {
  id: number
  vendor_order_id: string | null
  vendor_order_number: string | null
  created_at: Date
  url: string
  total_formatted?: string
  status_text: string
  items?: CartItemResponse[]
  shipping_address?: {
    email: string
    address_1: string
    address_2: string
    city: string
    country: string
    first_name: string
    last_name: string
    phone: string
    state: string
    zip_code: string
  }
  refunds: Refund[]
  metadata: unknown
}

export interface OrderExtendedResponse extends OrderResponse {
  card_type: string
  last4: string
  card_image: string
  subtotal_formatted: string
  discount?: {
    amount: number
    amount_formatted: string
  }
  tax_amount_formatted: string
  shipping_amount: number
  shipping_amount_formatted: string
  vendor_name: string
  vendor_shipping_information: string
  small_order_fee_formatted: string
  status_url?: string
  ship_tracking_url?: string
}

export interface InitResponse {
  algolia_app_id: string
  algolia_index_name_prefix: string
  algolia_index_name: string
  algolia_key: string
  algolia_key_v2: string
  current_vendor: ut_shop_vendors_name // Deprecated
  environment: string
  catalog_item_ratings: Record<string, number>
  vendor_name: string // Deprecated
  vendor_email: string // Deprecated
  vendor_phone: string | undefined // Deprecated
  needs_accept_terms: boolean
  personalized_box_enabled: boolean
  personalized_box_image_url: string
  merchant_country_code: string // Deprecated
  currency_code: string
  currency_symbol: string
  min_version: string
  google_maps_components_config: GoogleMapsComponentsConfig
  category_visibility: Record<ut_catalog_items_type, boolean>
  minimum_order_value?: number // Deprecated
  minimum_order_value_formatted?: string // Deprecated
  free_shipping_threshold?: number // Deprecated
  free_shipping_threshold_formatted?: string // Deprecated
  small_order_fee?: {
    // Deprecated
    amount: number
    amount_formatted: string
    threshold: number
    threshold_formatted: string
  }
  key_image_setting?: {
    image_url?: string
    url?: string
  }
  vendor_id: number
  is_managed_checkout?: boolean // Deprecated
  address_management_enabled: boolean
  payment_management_enabled: boolean
  supported_countries: {
    code: string
    name: string
    country_id: number
  }[]
}

export interface ShopApiError {
  error?: string
  meta?: unknown
  type?: string
}

export interface CoreApiError {
  error: string
  meta?: unknown
  type?: string
}

export interface AddressResponse {
  address_1: string
  address_2: string | null
  city: string
  country: string
  created_at: string
  first_name: string
  id: number
  is_default: boolean
  last_name: string
  phone: string | null
  special_instructions: string | null
  state: string
  updated_at: string
  zip_code: string
  user_id: number
}

export interface CartResponse {
  id: number
  vendor_cart_id: string
  count: number
  items: CartItemResponse[]
  qty: number
  subtotal: number
  subtotal_formatted: string
  subtotal_without_deposits?: number
  subtotal_without_deposits_formatted?: string
  free_shipping_without_deposits_threshold?: number
  free_shipping_without_deposits_threshold_formatted?: string
  discount?: {
    code: string
    amount: number
    amountFormatted: string
  }
  estimated_shipping_formatted?: string
  estimated_total_formatted?: string
  vendor_shipping_information: string
  shipping_address: {
    first_name?: string
    last_name?: string
    address_1: string
    address_2?: string
    city: string
    state: string
    country: string
    zip_code: string
    phone: string
    email: string
  }
  type: ut_shop_vendors_name | 'Shop__Cart__HalfTime'
  debug?: unknown
  checkout_url?: string
}

export interface AddToCartResponse {
  item_id: number | string
  name: string
  price: number
  qty: number
  sku: string
  qty_requested_not_in_stock?: boolean
}

export interface BulkAddToCartResponse {
  success: number
  error: number
}

export type PersonalizedBoxRecommendation = AlgoliaCatalogItem & {
  sku: string
  price: number
  price_formatted: string
  size: string
}

export interface StripeSetupIntentResponse {
  setup_intent_id: string
  setup_intent: string | null
  ephemeral_key?: string
  customer_id: string
  publishable_key: string
  merchant_country_code: string
  currency_code: string
}

export interface SubmitOrderResponse {
  order: {
    id: string
    status: string
    vendor_order_id: string
    metadata: {
      items: CartItemResponse[]
    }
  }
}

export interface BrewerySearch {
  objectID: number
  brewery_id: number
  brewery_name: string
  brewery_index: string
  brewery_alias: string[]
  brewery_beer_count: number
  brewery_image: string
  brewery_page_url: string
  brewery_country: string
  brewery_popularity: number
  brewery_out_of_business: number
  brewery_city: string
  brewery_state: string
  brewery_type_id: number
  brewery_name_sort: string
  brewery_homebrewery: number
  brewery_name_exact: string
  _geoloc: {
    lat: number
    lng: number
  }
}

export interface BeerSearch {
  objectID: number
  bid: number
  beer_abv: number
  beer_name: string
  beer_index: string
  brewery_label: string
  brewery_name: string
  brewery_id: number
  type_name: string
  type_id: number
  parent_style_id?: number
  parent_style_is_beer?: number
  thc_mg?: number | null
  cbd_mg?: number | null
  homebrew: number
  in_production: number
  popularity: number
  beer_slug?: string
  alias_alt: string[]
  spelling_alt: string[]
  brewery_alias: string[]
  beer_label: string
  beer_label_hd: string | null
  beer_index_short: string
  beer_name_sort: string
  brewery_name_sort: string
  rating_score: number
  rating_count: number
  brewery_beer_name: string
  _geoloc: {
    lat: number
    lng: number
  }
  index_date: string
}

export interface VendorSelectResponse {
  vendor: ut_shop_vendors_name
  country_id: number
  shipping_to_text: string
  is_beer_available: boolean
}
